import request from '../utils/request';
import { getApiEndpoint } from '../utils/config';

// Token过期前5分钟开始检查
const TOKEN_REFRESH_THRESHOLD = 5 * 60 * 1000; // 5分钟(毫秒)
// Token过期前1分钟显示倒计时
const TOKEN_COUNTDOWN_THRESHOLD = 60 * 1000; // 1分钟(毫秒)

class AuthService {
  constructor() {
    this.tokenTimer = null;
    this.countdownTimer = null;
    this.lastActivityTime = Date.now();
    this.countdownCallback = null;
  }

  // 解析JWT token
  parseJwt(token) {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => 
        '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
      ).join(''));
      return JSON.parse(jsonPayload);
    } catch (e) {
      return null;
    }
  }

  // 获取token过期时间
  getTokenExpiryTime(token) {
    const decoded = this.parseJwt(token);
    return decoded ? decoded.exp * 1000 : null; // 转换为毫秒
  }

  // 检查token是否需要刷新
  needsRefresh(token) {
    const expiryTime = this.getTokenExpiryTime(token);
    if (!expiryTime) return false;
    
    const timeUntilExpiry = expiryTime - Date.now();
    return timeUntilExpiry <= TOKEN_REFRESH_THRESHOLD;
  }

  // 记录用户活动
  recordActivity() {
    this.lastActivityTime = Date.now();
  }

  // 检查最近是否有用户活动
  hasRecentActivity() {
    return Date.now() - this.lastActivityTime < TOKEN_REFRESH_THRESHOLD;
  }

  // 刷新token
  async refreshToken() {
    try {
      const token = localStorage.getItem('token');
      const apiEndpoint = getApiEndpoint();
      
      const response = await fetch(`${apiEndpoint}/api/refresh_token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Token refresh failed');
      }
      
      const data = await response.json();
      const newToken = data.token;
      localStorage.setItem('token', newToken);
      this.setupTokenRefresh(newToken);
      return newToken;
    } catch (error) {
      console.error('Token refresh failed:', error);
      this.logout();
      return null;
    }
  }

  // 设置token刷新定时器
  setupTokenRefresh(token) {
    if (this.tokenTimer) {
      clearInterval(this.tokenTimer);
    }
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
    }

    const expiryTime = this.getTokenExpiryTime(token);
    if (!expiryTime) return;

    // 设置定时检查
    this.tokenTimer = setInterval(() => {
      if (this.hasRecentActivity()) {
        this.refreshToken();
      }
    }, 30000); // 每30秒检查一次

    // 设置倒计时
    this.setupCountdown(expiryTime);
  }

  // 设置倒计时
  setupCountdown(expiryTime) {
    const startCountdown = () => {
      const timeUntilExpiry = expiryTime - Date.now();
      
      if (timeUntilExpiry <= TOKEN_COUNTDOWN_THRESHOLD) {
        if (this.countdownCallback) {
          const secondsLeft = Math.max(0, Math.floor(timeUntilExpiry / 1000));
          this.countdownCallback(secondsLeft);
        }
      }

      if (timeUntilExpiry <= 0) {
        this.logout();
      }
    };

    this.countdownTimer = setInterval(startCountdown, 1000);
  }

  // 设置倒计时回调
  setCountdownCallback(callback) {
    this.countdownCallback = callback;
  }

  // 登出
  async logout() {
    try {
      const token = localStorage.getItem('token');
      const apiEndpoint = getApiEndpoint();
      
      await fetch(`${apiEndpoint}/api/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      if (this.tokenTimer) {
        clearInterval(this.tokenTimer);
      }
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer);
      }
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
  }

  // 初始化token监控
  initTokenMonitor() {
    const token = localStorage.getItem('token');
    if (token) {
      this.setupTokenRefresh(token);
    }

    // 监听用户活动
    const activities = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    activities.forEach(event => {
      window.addEventListener(event, () => this.recordActivity());
    });
  }

  // 用户注册 (需要认证)
  async register(userData) {
    try {
      const response = await request.post('/api/register', {
        user: userData
      });
      return { success: true, data: response };
    } catch (error) {
      console.error('Registration failed:', error);
      return { success: false, error: error.error || '注册失败' };
    }
  }

  // 修改密码
  async changePassword(oldPassword, newPassword) {
    try {
      const response = await request.post('/api/change_password', {
        old_password: oldPassword,
        new_password: newPassword
      });
      return { success: true, data: response };
    } catch (error) {
      console.error('Password change failed:', error);
      return { success: false, error: error.error || '密码修改失败' };
    }
  }
}

export default new AuthService();
